<template>
	<canvas class="calendar-canvas" :width="width" :height="height" :style="canvasStyle" ref="canvas"
		@wheel="wheel"
		@pointermove="pointerMove"
		@pointerenter="pointerEnter"
		@pointerleave="pointerLeave"
		@pointerup="pointerUp"
		@pointerdown="pointerDown"
		@click="click"

		@touchstart="touchStart"
		@touchmove="touchMove"
		@touchend="touchEnd"

		@drag="drag"
		@dragstart="dragStart"
		@dragend="dragEnd"
		@dragenter="dragEnter"
		@dragleave="dragLeave"
		@dragover="dragOver"
		@drop="drop"
	></canvas>
	<ui-root v-if="!secondary && reallyShowDialog"
			   :part="dialogForm"
			   :data="dialogData"
			   :errs="dialogErrors"
			   :readonly="dialogReadOnly"
			   :viewmode="dialogReadOnly"
			   :display="dialogDisplay"
			   :actions="dialogControls"
			   @close="cs.dialogClosed"
			   ref="dialog"></ui-root>
</template>

<script setup lang="ts">
import {ref, provide, watch, onUnmounted} from "vue";
import {type CalendarView, initialView} from "@/event/calendar/CalendarModel.ts";
import {injectEnv, provideEventBus, provideNewEventBus} from "@fd/Environment.ts";
import {useRoot} from "@/event/calendar/dom/CanvasRootComponent.ts";
import {ViewComponent} from "@/event/calendar/all/ViewComponent.ts";
import {CalendarStore, useCalendarStore} from "@/store/CalendarStore.ts";
import FormPart from "@fd/containers/FormPart.vue";
import {storeToRefs} from "pinia";
import UiRoot from "@fd/containers/UiRoot.vue";
import {UnregisterHandler} from "@fd/Types.ts";

const props = defineProps<{
	/**
	 * This name is used to save/remember last state of the calendar. If you don't provide it
	 * the state won't be saved.
	 */
	name: string;

	logicalWidth: number;
	logicalHeight: number;

	/** Display string. Determines what will be displayed inside the event box */
	display: string;
	/** Show attendees using monogram, full name or not at all*/
	showAttendees: "no"|"mono"|"full";

	/** Determine working hours */
	minTime: number;
	maxTime: number;

	snapMinutes: number;
	snapMonday: boolean;
	showTime:boolean;
	autocomplete:boolean;
	eventAlarm:number;
	wholeDayEventAlarm:number;
	secondary?: boolean;
}>();

const env = injectEnv();
const canvas = ref<HTMLCanvasElement | null>(null);
const { width, height, root, canvasStyle, wheel, pointerMove, pointerEnter, pointerLeave,
	pointerUp, pointerDown, click, dirty, dragStart, dragEnd, drag, dragEnter, dragLeave,
	touchStart, touchMove, touchEnd, dragOver, drop } = useRoot(canvas, props, env);

const cs = useCalendarStore();
const {dirtyData, isDialog, dialogForm, dialogData, dialogReadOnly, dialogDisplay, dialogControls, dialogErrors } = storeToRefs(cs);

let unregCommands:UnregisterHandler|null = null;

if (!props.secondary) {
	// Only for primary calendar
	const bus = provideNewEventBus();

	unregCommands = bus.onAnyCommand((cmd, ...extraArgs) => {
		return cs.dialogAction(cmd, ...extraArgs);
	});

	watch (() => isDialog.value, (show) => {
		if (show) reallyShowDialog.value = true;
		else {
			// dialog is closing
			if (dialog.value) {
				dialog.value.closeDialog();
				setTimeout(() => reallyShowDialog.value = false, 400);	// remove from DOM after animation
			} else {
				reallyShowDialog.value = false;
			}
		}
	});
}

watch(() => dirtyData.value, () => {
	if (typeof dirty.value === 'function') window.requestAnimationFrame(dirty.value);
});

const view:CalendarView = initialView(cs as CalendarStore, props.name, props.display, props.showAttendees, props.minTime, props.maxTime,
	props.snapMinutes, props.snapMonday, props.showTime, props.autocomplete, props.eventAlarm, props.wholeDayEventAlarm);

watch(root, (r) => {
	if (r) r.append(new ViewComponent(view));
});

const reallyShowDialog = ref<boolean>(isDialog.value);
const dialog = ref(null);

onUnmounted(() => {
	if (unregCommands) unregCommands();
});
</script>

<style>
.calendar-canvas {
	/*touch-action: none; /* zakáže veškeré defaultní touch akce */
	-webkit-user-select: none; /* Safari */
	user-select: none;
	-webkit-touch-callout: none; /* zabrání kontextovému menu na iOS */
}
</style>